import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getKontoAufschlag, setKontoAufschlag, type KontoAufschlagZeile } from '@/api/priceRollout'
import { Panel } from '@/components/Panel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { kontoKurz } from '@/lib/rollout-klartext'
import { fmtEUR } from '@/lib/pricing-format'

function KontoZeile({ k }: { k: KontoAufschlagZeile }) {
  const qc = useQueryClient(),
    [wert, setWert] = useState(String(k.aufschlag_ct)),
    [offen, setOffen] = useState(false)
  const n = Number(wert),
    gueltig = /^\d+$/.test(wert) && Number.isInteger(n) && n >= 0 && n <= 99
  const save = useMutation({
    mutationFn: () => setKontoAufschlag(k.account, n),
    onSuccess: () => {
      setOffen(false)
      toast.success(`Aufschlag ${kontoKurz(k.account)} gespeichert`)
      void qc.invalidateQueries({ queryKey: ['konto-aufschlag'] })
    },
    onError: () => toast.error('Aufschlag konnte nicht gespeichert werden'),
  })
  return (
    <tr className="border-t border-line-2 align-top">
      <td className="py-3 font-medium">{kontoKurz(k.account)}</td>
      <td className="py-3">
        <div className="flex items-center gap-1">
          <Input
            aria-label={`Aufschlag ${kontoKurz(k.account)}`}
            type="number"
            min={0}
            max={99}
            step={1}
            value={wert}
            onChange={(e) => setWert(e.target.value)}
            className="w-20 font-mono tabular-nums"
          />{' '}
          ct
        </div>
        {!gueltig && <div className="text-xs text-coral">Ganze Cent von 0 bis 99</div>}
        <div className="text-xs text-muted font-mono tabular-nums">
          {fmtEUR(12.5)} → {fmtEUR(12.5 + (gueltig ? n : 0) / 100)}
        </div>
      </td>
      <td className="py-3 text-right font-mono tabular-nums">{k.varianten_aktiv}</td>
      <td className="py-3 text-right font-mono tabular-nums">{k.rollouts_offen}</td>
      <td className="py-3 text-xs">
        {k.rollouts_veraltet > 0 && (
          <div className="text-warn">
            {k.rollouts_veraltet} mit altem Wert – wirkt beim nächsten Start oder Sammelstart
          </div>
        )}
        {k.rollouts_gekappt > 0 && (
          <div className="text-muted">{k.rollouts_gekappt} gekappt am Höchstpreis</div>
        )}
      </td>
      <td className="py-3 text-right">
        <Button
          size="sm"
          disabled={!gueltig || n === k.aufschlag_ct || save.isPending}
          onClick={() => setOffen(true)}
        >
          Aufschlag speichern
        </Button>
        <Dialog open={offen} onOpenChange={setOffen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Aufschlag ändern</DialogTitle>
              <DialogDescription>
                Aufschlag {kontoKurz(k.account)} von {k.aufschlag_ct} auf {n} ct ändern? Bestehende
                Preise ändern sich erst beim nächsten Start oder Sammelstart.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOffen(false)}>
                Abbrechen
              </Button>
              <Button disabled={!gueltig || save.isPending} onClick={() => save.mutate()}>
                Aufschlag speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </td>
    </tr>
  )
}
function MehrfachHinweis({ mehrfach }: { mehrfach: { anzahl: number; beispiele: string[] } }) {
  if (mehrfach.anzahl === 0) return null
  return (
    <div className="mt-3 text-sm text-ink-2">
      {mehrfach.anzahl} Varianten liegen auf mehreren eBay-Konten – dort gilt kein Aufschlag.
      <details>
        <summary className="cursor-pointer">Beispiele anzeigen</summary>
        <p>{mehrfach.beispiele.join(', ')}</p>
      </details>
    </div>
  )
}

export function KontoAufschlagPanel() {
  const q = useQuery({ queryKey: ['konto-aufschlag'], queryFn: getKontoAufschlag })
  return (
    <Panel
      title="Konto-Aufschlag eBay"
      subtitle="Fester Cent-Aufschlag aufs Rollout-Ziel je eBay-Konto. Gleiche Artikel auf verschiedenen Konten tragen so unterschiedliche Preise. Ausnahmen stehen unten."
    >
      {q.isLoading ? (
        <p>Lade Konto-Aufschläge…</p>
      ) : q.isError ? (
        <p>
          Konto-Aufschläge konnten nicht geladen werden.{' '}
          <Button variant="ghost" onClick={() => void q.refetch()}>
            Erneut laden
          </Button>
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm [&_td]:px-2 [&_th]:px-2">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th>Konto</th>
                  <th>Aufschlag</th>
                  <th className="text-right">Varianten aktiv</th>
                  <th className="text-right">Offene Rollouts</th>
                  <th>Hinweise</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {q.data?.konten.map((k) => (
                  <KontoZeile key={k.account} k={k} />
                ))}
              </tbody>
            </table>
          </div>
          {q.data && <MehrfachHinweis mehrfach={q.data.mehrfach_konten} />}
        </>
      )}
    </Panel>
  )
}
